import { useRouter } from 'next/router';

import { Box, Text, Tooltip } from '@zillow/constellation-10';

import { ClientAdProductType } from '@zg-rentals/ts-rental-manager-api-client';

import numberUtils from '../../../../utils/numberUtils';
import { useScreenSize } from '../../../../hooks';
import { useGetListingDetails } from '../../../../hooks/queries/properties';

import PropertyConstants from '../../../../constants/PropertyConstants';

import { mobileOnlyVerticalDividerStyles, priceOnlyContainerStyles } from '../styles';
import NetworkView from '../../../OverviewPage/modules/Listing/NetworkView';

const unitOfMeasureMapping: Record<string, string> = {
  listings: 'month',
  days: 'day',
};

export const AdditionalInformation = () => {
  const router = useRouter();
  const { isScreenDesktop, isScreenMobile } = useScreenSize();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const status = listingDetails?.syndication?.overview?.status;

  const renderPriceComponent = (): JSX.Element | null => {
    const hidePrice = !listingDetails?.active && !listingDetails?.updateActiveStatus?.isAllowed;
    const { isActive, details: contractDetails } = listingDetails?.singleFamilyContractDto ?? {};
    const { unitCost, unitOfMeasure = '' } = contractDetails ?? {};
    const formattedPrice = numberUtils.asCurrencyWithDefault(unitCost ?? 0, '-');

    if (
      !isActive ||
      hidePrice ||
      !contractDetails?.unitCost ||
      !(
        contractDetails?.productType === ClientAdProductType.SINGLE_FAMILY ||
        contractDetails?.productType === ClientAdProductType.SINGLE_FAMILY_DAILY_LISTINGS
      )
    ) {
      return null;
    }

    let priceText: string | null = null;

    if (unitOfMeasureMapping[unitOfMeasure] === 'day') {
      const chargeDetails = listingDetails?.isMultifamily ? ' per unit' : '';
      priceText = `If you publish this listing, you’ll be billed ${formattedPrice} per day ${chargeDetails},
            the rate in your Feed Connect™ contract.`;
    } else if (unitOfMeasureMapping[unitOfMeasure] === 'month') {
      const chargeDetails = listingDetails?.isMultifamily ? 'per unit for any units' : 'per average listing';
      priceText = `If you publish this listing, it will be added to your monthly invoice where you are charged
            ${formattedPrice}/month ${chargeDetails} live in the month.`;
    }

    return (
      <Box css={priceOnlyContainerStyles}>
        <Box css={{ display: 'flex' }}>
          <Text textStyle="body-sm-bold" css={{ fontSize: { lg: 'fontSizes.heading-xs' } }}>
            {formattedPrice}
          </Text>
          &nbsp;
          <Text asChild textStyle="body-xs" css={{ fontSize: { lg: 'fontSizes.body-sm' }, lineHeight: { lg: '30px' } }}>
            <span>{`/${unitOfMeasureMapping[unitOfMeasure]}`}</span>
          </Text>
          &nbsp;
          <Tooltip.Root placement="top">
            <Tooltip.Trigger />
            <Tooltip.Portal>
              <Tooltip.Content>
                <Text textStyle="body-sm">{priceText}</Text>
                <Tooltip.Arrow />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Box>
        <Box css={mobileOnlyVerticalDividerStyles} />
      </Box>
    );
  };

  const renderFeedConnectAdditionalInformation = () => {
    if (status === PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PARTIALLY_PUBLISHED) {
      return (
        <Box css={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <NetworkView
            isMultifamily={listingDetails?.isMultifamily ?? false}
            syndication={listingDetails?.syndication}
            label={`View published ${isScreenMobile ? '' : 'listings'}`}
          />
          {isScreenDesktop && renderPriceComponent()}
        </Box>
      );
    }

    if (
      status === PropertyConstants.SYNDICATION_OVERVIEW_STATUS.DECLINED ||
      status === PropertyConstants.SYNDICATION_OVERVIEW_STATUS.INACTIVE ||
      (status === PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PUBLISHED && !isScreenMobile) ||
      (status === PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PUBLISHING && !isScreenMobile)
    ) {
      return renderPriceComponent();
    }

    return null;
  };

  return listingDetails?.singleFamilyContractDto?.isRequired ? (
    renderFeedConnectAdditionalInformation()
  ) : (
    <>
      {status === PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PARTIALLY_PUBLISHED && (
        <Box css={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <NetworkView
            isMultifamily={listingDetails?.isMultifamily ?? false}
            syndication={listingDetails?.syndication}
            label={`View published ${isScreenMobile ? '' : 'listings'}`}
          />
        </Box>
      )}
      {listingDetails?.adproduct?.isUnpaid && (
        <Text textStyle="body-bold" css={{ marginRight: '600' }}>
          {listingDetails?.adproduct?.productTypesDisplayText?.title}
        </Text>
      )}
    </>
  );
};
